import { Suit, type Card } from "../gen/bazivan/v1/table_pb";
import { fa, isRed, suitSymbol } from "../game/cards";
import { inCubic, inOutCubic, lerp, outBack, outCubic } from "../scene/ease";
import { flyCard, kill, tween, tweenRaw, wait, waitRaw } from "../scene/fly";
import {
  CARD_ASPECT, cardW, handCardTarget, place, playerCardW, playerHandTarget, seatAngleDeg, seatAnchor, trickCardW, trickPos, type Vec,
} from "../scene/geometry";
import { AZURE, CHROME_D, CHROME_M, CRIMSON, GLOW, TableView, norm } from "../scene/table";
import type { CutKind, HokmLocal } from "./state";

/** خانه‌ی یک کارتِ زمین + جابه‌جاییِ لحظه‌ای‌اش (هل، تپش، یکه‌خوردن). */
type TrickFx = { pos: Vec; rot: number; dx: number; dy: number; drot: number; scale: number };

/**
 * صحنه‌ی حکم — پورتِ HokmView.cs + HokmView.Impl.cs روی TableView.
 *
 * لایه‌ها به ترتیبِ Build(): Table → OppHands → Piles → Seats → Hand → (نشستِ
 * خودم، بالای دست) → Fly → Ui → Overlays. مختصات همان یونیتی است (مرکز، y
 * بالا)؛ `place()` برمی‌گردانَد.
 */
export type SceneCallbacks = {
  onCard(i: number): void;
  seatName(local: number): string;
};

const WIN_FRAME = "#ffd84a";

export class HokmScene extends TableView {
  private cb: SceneCallbacks;
  private trickCards: (HTMLElement | null)[] = [];
  private unitAnchor: number[] = [];
  private cardFx = new WeakMap<HTMLElement, TrickFx>();
  private lastSt: HokmLocal | null = null;
  private pilesTiles!: HTMLElement;
  private trumpBadge!: HTMLElement;
  private trumpHome: Vec = { x: 0, y: 0 };
  private trumpSuit: Suit | null = null;
  private hakemEls: { mark: HTMLElement; suit: HTMLElement }[] = [];

  constructor(host: HTMLElement, count: number, cb: SceneCallbacks) {
    super(host, count, "hkm");
    this.cb = cb;
    this.build();
  }

  protected seatDFactor(): number { return 0.26; }
  protected plateH(): number { return 58; }

  protected buildCenter(): void {
    this.trickCards = new Array(this.count).fill(null);
    this.pilesTiles = document.createElement("div");
    this.pilesTiles.className = "tiles";
    this.piles.appendChild(this.pilesTiles);
    this.buildTrumpBadge();
  }

  protected redraw(): void {
    if (!this.lastSt) return;
    const st = this.lastSt;
    this.layoutSeats(st); this.render(st); this.renderTrick(st); this.updateScores(st); this.setTurn(st);
  }

  /** مدالِ حکم (BuildCenter): دیسکِ ۱۰۴ واحدی، ستونِ چپ، خاموش تا اعلام. */
  private buildTrumpBadge(): void {
    const d = CHROME_D;
    this.trumpHome = { x: -this.sc.halfW + CHROME_M + d / 2, y: this.sc.halfH - CHROME_M - d / 2 - 22 };
    const b = document.createElement("div");
    b.className = "hkm-trump";
    b.style.width = b.style.height = `${d}px`;
    b.innerHTML = `<i class="glow"></i><i class="disc"></i><span class="g" style="font-size:${d * 0.58}px"></span><span class="cap" style="width:${d * 0.62}px">حکم</span>`;
    this.ui.appendChild(b);
    this.trumpBadge = b;
    this.placeTrump(this.trumpHome);
    if (this.trumpSuit !== null) this.showTrump(this.trumpSuit); else b.hidden = true;
  }

  /**
   * جای مدالِ حکم را با ویژگیِ `translate` می‌گذارد، نه با `transform`.
   *
   * زنگوله با `rotate` می‌چرخد، و مرورگر زنجیره را translate→rotate→transform
   * می‌بندد: با جای‌گذاریِ transform، اول مدال به گوشه می‌رفت و بعد **دورِ مرکزِ
   * صفحه** می‌چرخید — یعنی هر زنگوله نزدیک صد پیکسل از گوشه می‌پرید. با
   * translateِ جدا ترتیب برعکس می‌شود: چرخش در جای خود، بعد جابه‌جایی.
   */
  private placeTrump(pos: Vec, rotDeg = 0): void {
    const b = this.trumpBadge;
    b.style.translate = `calc(-50% + ${pos.x}px) calc(-50% + ${-pos.y}px)`;
    b.style.rotate = rotDeg ? `${-rotDeg}deg` : "";
  }

  private showTrump(suit: Suit): void {
    const b = this.trumpBadge;
    b.querySelector(".g")!.textContent = suitSymbol[suit] ?? "";
    b.classList.toggle("red", isRed(suit));
    b.hidden = false;
    b.classList.add("bell");
    this.trumpSuit = suit;
  }

  /**
   * مدالِ حکم را با وضعیت یکی می‌کند — از **هر** وضعیتی، نه یک بار سرِ اعلام.
   *
   * پیش از این مدال لبه‌ای بود: فقط رویدادِ «حکم اعلام شد» و یک شاخه‌ی
   * «اگر هنوز نشان داده نشده» می‌نوشتندش. آن شاخه یعنی همان یک وضعیتِ بینِ
   * دو دست — که trumpChosenش false است — بارِ کلِ درست‌ماندنِ مدال را
   * می‌کشید، و هر جا که از دست می‌رفت مدال تا آخرِ دست خالِ دستِ **پیش** را
   * نشان می‌داد و هیچ وضعیتی هم دیگر درستش نمی‌کرد: صفی که بیش از ۱۲ ثانیه
   * عقب افتاده و تا تازه‌ترین وضعیت جلو می‌پرد ([[Sequencer.catchUp]])، و
   * بازگشت از قطعی که فقط وضعیت می‌گیرد و رویداد نه ([[Table.resync]]).
   *
   * حالا مدال از وضعیت **از نو** ساخته می‌شود. تکراری که باشد دست نمی‌خورد،
   * وگرنه زنگوله‌ی مراسم را هر وضعیت پاک می‌کرد.
   */
  syncTrump(chosen: boolean, suit: Suit): void {
    if (!chosen) {
      this.trumpSuit = null;
      this.trumpBadge.hidden = true;
      this.trumpBadge.classList.remove("bell");
      return;
    }
    if (this.trumpSuit === suit) return;
    this.showTrump(suit);
    this.trumpBadge.classList.remove("bell");
    this.placeTrump(this.trumpHome);
  }

  // ---------------------------------------------------------------- نشست‌ها

  /** نشانِ حاکم: هاله‌ی ۱٫۳۶d + حلقه‌ی ۱٫۱۲d زیرِ آواتار، تاجِ ۰٫۵۸d سوار بر لبه، پلاکِ «حاکم» رو به مرکز. */
  protected decorateSeat(l: number, root: HTMLElement, d: number, pos: Vec): void {
    const hakem = document.createElement("div"); hakem.className = "hakem"; hakem.hidden = true;
    const cd = d * 0.58, cUp = d * 0.5 + d * 0.06 + cd * 0.42;
    const dir = l === 0 ? { x: 1, y: 0 } : norm({ x: -pos.x, y: -pos.y });
    const pw = d * 1.05, ph = 30;
    const pp = Math.abs(dir.x) > 0.5
      ? { x: dir.x * (d / 2 + pw / 2 + 8), y: dir.y * (d / 2 + pw / 2 + 8) }
      : { x: dir.x * (d / 2 + ph / 2 + 10), y: dir.y * (d / 2 + ph / 2 + 10) };
    hakem.innerHTML =
      `<i class="glow" style="inset:${-d * 0.18}px"></i><i class="hring" style="inset:${-d * 0.06}px"></i>` +
      `<span class="crown" style="font-size:${cd}px;transform:translate(-50%,-50%) translateY(${-cUp}px)">♛</span>` +
      `<span class="pill" style="width:${pw}px;height:${ph}px;transform:translate(-50%,-50%) translate(${pp.x}px,${-pp.y}px)"><b class="hs"></b><span>حاکم</span></span>`;
    // زیرِ آواتار
    root.insertBefore(hakem, root.querySelector(".av"));
    this.hakemEls[l] = { mark: hakem, suit: hakem.querySelector(".hs")! };
  }

  layoutSeats(st: HokmLocal): void {
    this.lastSt = st;
    this.hakemEls = [];
    this.layoutSeatsFrom({ names: st.names, bots: st.bots, avatars: st.avatars, subscribers: st.subscribers, places: st.places, connected: st.connected, watching: st.watching });
  }

  setTurn(st: HokmLocal): void {
    for (let l = 0; l < this.seatEls.length; l++) {
      const s = this.seatEls[l]!;
      const on = !st.handOver && !st.matchOver && ((st.trumpChosen && st.turn === l) || (st.awaitingTrump && st.hakem === l));
      this.setRing(l, on);
      const h = this.hakemEls[l];
      if (h) {
        h.mark.hidden = !(st.hakem === l);
        h.suit.textContent = st.trumpChosen ? (suitSymbol[st.trump] ?? "") : "";
        h.suit.classList.toggle("red", st.trumpChosen && isRed(st.trump));
      }
      if (s.count) s.count.textContent = fa(st.counts[l] ?? 0);
      this.setConnected(l, !!st.connected[l]);
    }
  }

  // ---------------------------------------------------------------- رندر

  hideHand(hidden: boolean): void { this.handHidden = hidden; }

  /** Render: بادبزنِ حریف‌ها (پشت‌ورو) روی میز و بادبزنِ خودم پایینِ صفحه. */
  render(st: HokmLocal): void {
    this.lastSt = st;
    this.opp.innerHTML = "";
    this.hand.innerHTML = "";
    if (this.handHidden) return;
    const cw = cardW(this.sc);
    // تماشاچی دستی ندارد، پس صندلیِ ۰ هم بادبزنِ پشت‌وروی خودش را می‌گیرد.
    for (let l = st.watching ? 0 : 1; l < this.count; l++) {
      const n = st.counts[l] ?? 0;
      for (let j = 0; j < n; j++) {
        const { pos, rot } = handCardTarget(this.sc, l, this.count, n, j);
        const c = this.cardEl(null, true, cw);
        c.style.transform = place(pos, rot);
        c.style.zIndex = String(j);
        this.opp.appendChild(c);
      }
    }
    const pw = playerCardW(this.sc);
    const n = st.myHand.length;
    st.myHand.forEach((card, j) => {
      const { pos, rot } = playerHandTarget(this.sc, n, j);
      const c = this.cardEl(card, false, pw);
      c.style.transform = place(pos, rot);
      c.style.zIndex = String(j);
      c.classList.add("mine");
      this.armCard(c, this.cardLabelFor(card), () => this.cb.onCard(j));
      this.hand.appendChild(c);
    });
    this.armHand();
  }

  /** نشانِ «بازی‌شدنی» روی دستِ خودم — همان کاری که Render با AwaitingHumanPlay می‌کند. */
  markPlayable(ok: boolean[]): void {
    const cards = this.hand.querySelectorAll<HTMLElement>(".card.mine");
    cards.forEach((c, i) => {
      c.classList.toggle("playable", !!ok[i]);
      c.classList.toggle("dim", ok.some(Boolean) && !ok[i]);
    });
  }

  renderTrick(st: HokmLocal): void {
    for (let l = 0; l < this.count; l++) {
      kill(this.trickCards[l]); this.trickCards[l] = null;
      const card = st.trick[l];
      if (!card) continue;
      const f = this.cardEl(card, false, trickCardW(this.sc));
      this.setHome(f, trickPos(this.sc, l, this.count), seatAngleDeg(l, this.count) + 90);
      f.style.zIndex = "5";
      this.piles.appendChild(f);
      this.trickCards[l] = f;
    }
  }

  /**
   * خانه‌ی یک کارتِ زمین و جابه‌جاییِ لحظه‌ایِ آن.
   *
   * هل‌دادن (جا)، تپش (اندازه) و یکه‌خوردن (چرخش) می‌توانند هم‌زمان روی یک کارت
   * بدوند؛ اگر هر کدام مستقیم `transform` بنویسد آخری بقیه را پاک می‌کند. پس
   * همه روی همین حالت می‌نویسند و یک جا با هم ساخته می‌شود.
   */
  private setHome(el: HTMLElement, pos: Vec, rot: number): void {
    this.cardFx.set(el, { pos, rot, dx: 0, dy: 0, drot: 0, scale: 1 });
    this.applyFx(el);
  }

  private setFx(el: HTMLElement, patch: Partial<TrickFx>): void {
    const f = this.cardFx.get(el);
    if (!f) return;
    Object.assign(f, patch);
    this.applyFx(el);
  }

  private applyFx(el: HTMLElement): void {
    const f = this.cardFx.get(el);
    if (!f) return;
    el.style.transform = place({ x: f.pos.x + f.dx, y: f.pos.y + f.dy }, f.rot + f.drot, f.scale);
  }

  // ---- دسته‌های برد: کارتِ کوچکِ پشت‌ورو با شماره، کنارِ جمع‌کننده ----

  private pileTileW(): number { return cardW(this.sc) * 0.62; }

  private pilePos(seat: number): Vec {
    const a = seatAngleDeg(seat, this.count) * Math.PI / 180;
    const dir = { x: Math.cos(a), y: Math.sin(a) }, tan = { x: -Math.sin(a), y: Math.cos(a) };
    const r = seat === 0 ? 0.46 : 0.6;
    return { x: (dir.x * this.sc.tableR * r + tan.x * this.sc.tableR * 0.36) * this.sc.aspect,
             y: dir.y * this.sc.tableR * r + tan.y * this.sc.tableR * 0.36 };
  }

  /** بردهای بازیکنِ اصلی جلوی هم‌تیمی‌اش می‌نشیند — پایینِ صفحه جای دستِ خودش است. */
  private anchorFor(st: HokmLocal, winner: number): number {
    if (!st.teams || winner !== 0) return winner;
    for (let l = 1; l < st.n; l++) if (st.seatUnit[l] === st.seatUnit[0]) return l;
    return winner;
  }

  private pileSlot(seat: number, i: number, n: number): Vec {
    const a = seatAngleDeg(seat, this.count) * Math.PI / 180;
    const tan = { x: -Math.sin(a), y: Math.cos(a) };
    const step = this.pileTileW() * CARD_ASPECT * 0.62;
    const p = this.pilePos(seat);
    const m = i - (n - 1) / 2;
    return { x: p.x + tan.x * m * step, y: p.y + tan.y * m * step };
  }

  private ensureAnchors(st: HokmLocal): void {
    if (this.unitAnchor.length !== st.units) this.unitAnchor = new Array(st.units).fill(-1);
  }

  private pileTile(number: number): HTMLElement {
    const c = this.cardEl(null, true, this.pileTileW());
    c.classList.add("pile-tile");
    c.innerHTML = `<span class="num">${fa(number)}</span>`;
    return c;
  }

  private renderTrickPiles(st: HokmLocal): void {
    this.pilesTiles.innerHTML = "";
    this.ensureAnchors(st);
    if (!st.tricksWon.some((n) => n > 0)) { this.unitAnchor.fill(-1); return; }
    for (let u = 0; u < st.units; u++) {
      const n = st.tricksWon[u] ?? 0;
      if (n === 0) continue;
      let seat = this.unitAnchor[u] ?? -1;
      if (seat < 0) { for (let l = 0; l < st.n; l++) if (st.seatUnit[l] === u) { seat = this.anchorFor(st, l); break; } }
      if (seat < 0) seat = 0;
      for (let i = 0; i < n; i++) {
        const t = this.pileTile(i + 1);
        t.style.transform = place(this.pileSlot(seat, i, n));
        this.pilesTiles.appendChild(t);
      }
    }
  }

  updateScores(st: HokmLocal): void { this.renderTrickPiles(st); }

  // ---------------------------------------------------------------- انیمیشن‌ها

  /** AnimDealBatch: پخشِ پشت‌ورو و بعد رندرِ دست. */
  async animDealBatch(st: HokmLocal, startSeat: number, perPlayer: number): Promise<void> {
    this.handHidden = true; this.render(st);
    await this.dealBacks(startSeat, perPlayer);
    this.handHidden = false; this.render(st);
  }

  /** AnimPlayToTrick: از صندلی به جای خال، رو به صاحبش، ۲۸۰. */
  async animPlayToTrick(seat: number, card: Card): Promise<void> {
    const from = seat === 0 ? this.myOrigin() : seatAnchor(this.sc, seat, this.count);
    const face = this.flyEl(card, false, trickCardW(this.sc));
    await flyCard(face, from, trickPos(this.sc, seat, this.count), seatAngleDeg(seat, this.count) + 90, 280);
    kill(face);
  }

  /** AnimCollectTrick: کارتِ برنده بالا می‌آید با قابِ زرد، مکث، بعد همه به سمتِ برنده جمع می‌شوند. */
  async animCollectTrick(st: HokmLocal, winner: number): Promise<void> {
    this.ensureAnchors(st);
    const unit = st.seatUnit[winner] ?? 0;
    if ((this.unitAnchor[unit] ?? -1) < 0) this.unitAnchor[unit] = this.anchorFor(st, winner);

    const win = this.trickCards[winner];
    if (win) {
      win.style.zIndex = "9";
      win.classList.add("win");
      const p0 = trickPos(this.sc, winner, this.count), p1 = { x: p0.x * 0.55, y: p0.y * 0.55 };
      const r0 = seatAngleDeg(winner, this.count) + 90;
      await tween(220, (k) => {
        const e = outBack(k);
        win.style.transform = place({ x: lerp(p0.x, p1.x, e), y: lerp(p0.y, p1.y, e) }, lerp(r0, 0, k), 1 + 0.28 * e);
        win.style.setProperty("--frame", String(outCubic(k)));
      });
      void this.pulse(p1, WIN_FRAME, this.sc.tableR * 0.2, this.sc.tableR * 0.7, 450, 0.35);
      await wait(420);
    }

    const to = this.seatFx(winner);
    void this.pulse(to, GLOW, this.sc.tableR * 0.15, this.sc.tableR * 0.6, 500, 0.4);
    let order = 0;
    const flights: Promise<void>[] = [];
    for (let k = 0; k < this.count; k++) {
      const i = (winner + 1 + k) % this.count;   // برنده آخر می‌رود
      const card = this.trickCards[i];
      if (!card) continue;
      this.trickCards[i] = null;
      this.fly.appendChild(card);
      const from = this.currentPos(card);
      const delay = 40 * order++;
      flights.push(wait(delay).then(() => tween(320, (k2) => {
        const p = inOutCubic(k2);
        card.style.transform = place({ x: lerp(from.x, to.x, p), y: lerp(from.y, to.y, p) }, lerp(0, 25, p), lerp(1, 0.3, p));
        card.style.opacity = String(1 - Math.max(0, (k2 - 0.6) / 0.4));
      })).then(() => kill(card)));
    }
    await Promise.all(flights);
    await wait(60);
    this.renderTrickPiles(st);
  }

  /** AnimTrumpAnnounce: خالِ درشت وسطِ میز بزرگ می‌شود و محو؛ بعد مدال از چپ می‌آید. */
  async animTrumpAnnounce(suit: Suit): Promise<void> {
    const g = document.createElement("div");
    g.className = `hkm-announce ${isRed(suit) ? "red" : ""}`;
    g.textContent = suitSymbol[suit] ?? "";
    g.style.fontSize = `${this.sc.tableR * 0.9}px`;
    this.ov.appendChild(g);
    void this.pulse({ x: 0, y: this.sc.tableR * 0.05 }, GLOW, this.sc.tableR * 0.2, this.sc.tableR * 1.1, 500, 0.35);
    await tween(520, (k) => {
      const s = lerp(0.35, 1.55, outCubic(k));
      g.style.transform = place({ x: 0, y: this.sc.tableR * 0.05 }, 0, s);
      g.style.opacity = String(k < 0.15 ? outCubic(k / 0.15) : 1 - inCubic((k - 0.15) / 0.85));
    });
    kill(g);

    this.showTrump(suit);
    const b = this.trumpBadge;
    b.classList.remove("bell");
    const from = { x: -this.sc.halfW - 100, y: this.trumpHome.y };
    await tween(420, (k) => {
      const e = outBack(k);
      this.placeTrump({ x: lerp(from.x, this.trumpHome.x, e), y: this.trumpHome.y }, lerp(-18, 0, e));
    });
    this.placeTrump(this.trumpHome);
    b.classList.add("bell");
  }

  /**
   * AnimCut — پورتِ HokmView.AnimCut.
   *
   * سه ضرب دارد و هیچ پلاک و نوشته‌ای ندارد؛ تیغه، شکاف و نور خودشان می‌گویند
   * چه شد:
   *   ۱) ضربه: یک ایستِ کوتاه (HitStop) تا دست زیرِ ضربه حس شود.
   *   ۲) چاقو: خودِ خالِ حکم از یک سوی میز می‌آید و از سوی دیگر می‌رود و ردِ
   *      شکاف پشتِ سرش می‌مانَد. سرِ عبور از مرکز، خال دو نیم می‌شود: کارت‌های
   *      دو سوی خط **عمود بر خطِ برش** کنار می‌روند و کارتِ حکم بالا می‌آید.
   *   ۳) نور: چندمین بریدنِ این دست را رنگِ تپش می‌گوید — اولی آبی، از دومی
   *      به بعد قرمز.
   *
   * حرکت هر بار **یکی** است. پیش از این سربُری تیغه‌ی دومِ ضربدری داشت و
   * دو برابر طول می‌کشید؛ در دستی که چند بار بریده می‌شد، همین بازی را کند
   * می‌کرد. فرقِ برش‌ها را نور می‌گوید، نه یک حرکتِ اضافه.
   */
  async animCut(st: HokmLocal, seat: number, kind: CutKind, order: number): Promise<void> {
    if (kind === "none") return;
    const trump = st.trumpChosen ? st.trump : (st.trick[seat]?.suit ?? Suit.SPADES);
    const light = order <= 1 ? AZURE : CRIMSON;
    this.hitStop(55);

    const len = this.sc.tableR * 1.5;
    const ang = -28, rad = ang * Math.PI / 180;
    const dir = { x: Math.cos(rad), y: Math.sin(rad) };
    const normal = { x: -dir.y, y: dir.x };   // عمود بر خطِ برش

    const onCross = () => {
      for (let i = 0; i < this.trickCards.length; i++) {
        const c = this.trickCards[i];
        if (!c) continue;
        if (i === seat) {
          c.style.zIndex = "20";
          void this.punch(c, 0.28, 450);
          void this.cutFrame(c, 1400);
          continue;
        }
        // هر کارت به همان سویی می‌رود که خودش از خط ایستاده است
        const home = trickPos(this.sc, i, this.count);
        const side = Math.sign(home.x * normal.x + home.y * normal.y) || 1;
        void this.nudge(c, { x: normal.x * side * 12, y: normal.y * side * 12 }, 420);
      }
      void this.pulse({ x: 0, y: 0 }, light, this.sc.tableR * 0.12, this.sc.tableR * 0.7, 450, 0.5);
    };

    await this.blade(trump, len, ang, onCross);
    await wait(180);   // یک نفَس، تا کارتِ بعدی روی لحظه نیفتد
  }

  /**
   * چاقو: خالِ حکم (گلیفِ درشت با دورخطِ طلایی) در امتدادِ یک خط از میز می‌گذرد؛
   * پشتِ سرش «شکاف» می‌مانَد — خطِ تیره‌ای با لبه‌ی داغ که بعد سرد و محو می‌شود.
   * `onCross` سرِ عبور از مرکز صدا می‌شود.
   */
  private async blade(suit: Suit, len: number, angleDeg: number, onCross: (() => void) | null): Promise<void> {
    const rad = angleDeg * Math.PI / 180, dir = { x: Math.cos(rad), y: Math.sin(rad) };
    const p0 = { x: -dir.x * len / 2, y: -dir.y * len / 2 }, p1 = { x: dir.x * len / 2, y: dir.y * len / 2 };

    // شکاف — دو لایه: تیغه‌ی تیره‌ی روی نمد، و لبه‌ی روشنِ داغ درونش
    const scar = document.createElement("div");
    scar.className = "hkm-scar";
    scar.innerHTML = `<i class="hot"></i>`;
    scar.style.height = "7px";
    scar.style.width = "0px";
    // لنگرِ شکاف لبه‌ی چپش است، نه مرکزش: از سرِ ورودِ تیغه رشد می‌کند
    scar.style.transform = `translate(${p0.x}px, calc(${-p0.y}px - 50%)) rotate(${-angleDeg}deg)`;
    this.ov.appendChild(scar);
    const hot = scar.querySelector<HTMLElement>(".hot")!;

    // تیغه — خودِ خال، نوکش رو به جلو
    const g = this.sc.tableR * 0.46;
    const blade = document.createElement("div");
    blade.className = "hkm-blade";
    blade.textContent = suitSymbol[suit] ?? "";
    blade.style.fontSize = `${g}px`;
    blade.style.color = isRed(suit) ? "#c8364a" : "#1c1814";
    // دورخطِ نازک، هم‌وزنِ outlineWidth ۰٫۲۲ی TMP. کلفت‌تر که باشد خال توپُر
    // می‌شود و به‌جای تیغه، یک لکه‌ی سیاه دیده می‌شود.
    blade.style.webkitTextStroke = `${Math.max(1.5, g * 0.04)}px #e9cd7e`;
    this.ov.appendChild(blade);

    let crossed = false;
    await tween(320, (k) => {
      const p = inOutCubic(k);
      // وسطِ راه بزرگ‌تر می‌شود، و دو سرِ مسیر محو است
      blade.style.transform = place({ x: lerp(p0.x, p1.x, p), y: lerp(p0.y, p1.y, p) },
        angleDeg - 90, 0.85 + 0.35 * Math.sin(p * Math.PI));
      blade.style.opacity = String(Math.min(1, Math.max(0, Math.min(k * 6, (1 - k) * 4))));
      scar.style.width = `${len * p}px`;
      if (!crossed && p >= 0.5) { crossed = true; onCross?.(); }
    });
    if (!crossed) onCross?.();
    kill(blade);

    // شکاف سرد می‌شود: لبه‌ی داغ زود می‌رود، تیغه‌ی تیره دیرتر، و خط نازک می‌شود
    await tween(900, (k) => {
      hot.style.opacity = String(1 - outCubic(Math.min(1, k * 2.2)));
      scar.style.opacity = String(1 - inCubic(k));
      scar.style.height = `${lerp(7, 2, k)}px`;
    });
    kill(scar);
  }

  // ---- ریزحرکت‌های بریدن ----

  /** Nudge: کنار رفتنِ یک کارت از خطِ شکاف و بازگشتِ فنری به جای خودش. */
  private nudge(el: HTMLElement, offset: Vec, durMs: number): Promise<void> {
    return tween(durMs, (k) => {
      const a = k < 0.25 ? outCubic(k / 0.25) : 1 - outBack((k - 0.25) / 0.75);
      this.setFx(el, { dx: offset.x * a, dy: offset.y * a });
    }).then(() => this.setFx(el, { dx: 0, dy: 0 }));
  }

  /** Juice.Punch: برجستگیِ سریع و بعد نشستِ فنری. */
  private punch(el: HTMLElement, strength: number, durMs: number): Promise<void> {
    return tween(durMs, (x) => {
      const pop = x < 0.35 ? outCubic(x / 0.35) * strength : strength * (1 - outBack((x - 0.35) / 0.65));
      this.setFx(el, { scale: 1 + pop });
    }).then(() => this.setFx(el, { scale: 1 }));
  }

  /** CutFrame: قابِ طلاییِ کوتاه‌عمر دورِ کارتِ برنده‌ی لحظه — می‌آید و می‌رود. */
  private cutFrame(card: HTMLElement, lifeMs: number): Promise<void> {
    const frame = document.createElement("i");
    frame.className = "cutframe";
    card.appendChild(frame);
    const inD = 120, outD = 300;
    return tween(lifeMs, (k) => {
      const t = k * lifeMs;
      frame.style.opacity = String(
        t < inD ? outCubic(t / inD)
        : t > lifeMs - outD ? 1 - inCubic((t - (lifeMs - outD)) / outD)
        : 1);
    }).then(() => kill(frame));
  }

  /** AnimKot: تاریکیِ میز، بنرِ «کُت!»، مدال‌های دست، کپسولِ امتیاز. */
  async animKot(st: HokmLocal, winnerUnit: number, points: number, hakemKot: boolean): Promise<void> {
    const won = winnerUnit === st.myUnit;
    const n = Math.max(1, st.tricksWon[winnerUnit] ?? 1);
    void this.shake(420, 8);

    const dim = document.createElement("div"); dim.className = `hkm-dim ${won ? "won" : "lost"}`;
    this.ov.appendChild(dim);
    requestAnimationFrame(() => dim.classList.add("in"));

    const root = document.createElement("div"); root.className = `hkm-kot ${won ? "won" : "lost"}`;
    const title = won ? (hakemKot ? "پُرکُت!" : "کُت!") : (hakemKot ? "پُرکُت شدی!" : "کُت شدی!");
    const tricks = `${fa(n)} بر ${fa(0)}`;
    const sub = won ? `حریف یک دست هم نبرد — ${tricks} — ${fa(points)}+ امتیاز`
                    : `یک دست هم نبردی — ${tricks} — ${fa(points)}+ امتیاز برای حریف`;
    root.innerHTML = `<div class="bnr">${title}</div><div class="medals">${Array.from({ length: n }, () => `<span class="m">${won ? "✓" : "×"}</span>`).join("")}</div><div class="cap">${sub}</div>`;
    this.ov.appendChild(root);

    const banner = root.querySelector<HTMLElement>(".bnr")!;
    const fromX = this.sc.halfW + 400;
    await tweenRaw(360, (k) => { banner.style.transform = `translateX(${lerp(fromX, 0, outBack(k))}px)`; });
    const medals = root.querySelectorAll<HTMLElement>(".m");
    for (const m of medals) { m.classList.add("in"); await waitRaw(80); }
    root.querySelector(".cap")!.classList.add("in");
    await waitRaw(1500);
    root.classList.add("out"); dim.classList.remove("in");
    await waitRaw(280);
    kill(root); kill(dim);
  }
}
